import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getManagedEventsByBundles } from "@/features/supply-config/managed-events-api";
import { RescheduleBundleItem } from "@/features/bundles/services/create-reschedule-bundles";

// Reschedule bundle builder: lists every managed event in the region and lets
// the admin set a within-24h reschedule fee per event. Each filled row becomes
// one reschedule-type bundle. Rows left blank are skipped.
export default function RescheduleBundlesStep({
  regionId,
  productId,
  currency,
  onItemsChange,
}: {
  regionId: string;
  productId?: string;
  currency: string;
  onItemsChange: (items: RescheduleBundleItem[]) => void;
}) {
  // Available managed events = those referenced by this product's bundle
  // session_types in the region (what its sessions actually book).
  const { data: events = [], isLoading } = useQuery({
    queryKey: [
      "managed-events-by-bundles",
      { region_id: regionId, product_id: productId },
    ],
    queryFn: () =>
      getManagedEventsByBundles({
        region_id: regionId,
        product_id: productId || undefined,
      }),
    enabled: !!regionId,
  });

  // Per-event fee + optional name override, keyed by managed_event_id.
  const [rows, setRows] = useState<
    Record<string, { amount: string; name: string }>
  >({});

  // Emit valid items up (ref avoids re-running on parent callback identity).
  const onItemsChangeRef = useRef(onItemsChange);
  onItemsChangeRef.current = onItemsChange;
  useEffect(() => {
    const items: RescheduleBundleItem[] = [];
    for (const [managed_event_id, r] of Object.entries(rows)) {
      const raw = r.amount?.trim();
      if (!raw) continue;
      const num = Number(raw);
      if (!Number.isFinite(num) || num < 0) continue;
      items.push({
        managed_event_id,
        amount: Math.round(num),
        ...(r.name?.trim() ? { name: r.name.trim() } : {}),
      });
    }
    onItemsChangeRef.current(items);
  }, [rows]);

  const setRow = (id: string, patch: Partial<{ amount: string; name: string }>) =>
    setRows((prev) => ({
      ...prev,
      [id]: {
        amount: prev[id]?.amount ?? "",
        name: prev[id]?.name ?? "",
        ...patch,
      },
    }));

  if (!regionId) {
    return (
      <p className="text-sm text-gray-500">
        Pick a region in the Basics step first.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Reschedule fees</h3>
        <p className="text-sm text-gray-500">
          Set the within-24h reschedule fee for each managed event. One
          reschedule bundle is created per event with a fee entered; blank rows
          are skipped.
        </p>
      </div>

      {isLoading ? (
        <div className="h-24 animate-pulse rounded bg-gray-100" />
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-400">
          No managed events found — this product has no active session bundles in
          this region yet.
        </p>
      ) : (
        <div className="divide-y rounded-md border border-gray-200">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center gap-3 p-3 text-sm"
            >
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{ev.name}</p>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-400">
                  <Badge variant="outline">{ev.category}</Badge>
                  {ev.duration_minutes ? (
                    <span>{ev.duration_minutes} min</span>
                  ) : null}
                </div>
              </div>
              <Input
                className="w-28"
                placeholder="Name (optional)"
                value={rows[ev.id]?.name ?? ""}
                onChange={(e) => setRow(ev.id, { name: e.target.value })}
              />
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400">{currency}</span>
                <Input
                  className="w-24"
                  type="number"
                  min={0}
                  placeholder="Fee"
                  value={rows[ev.id]?.amount ?? ""}
                  onChange={(e) => setRow(ev.id, { amount: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
